import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { CommentEntity } from '@interpals/api-web';

import { createLike, deleteLike } from '../likes/actions';

import {
    createComment,
    deleteComment,
    getCommentReplies,
    getPhotoComments,
    getPostComments,
    moderationDeleteComment,
} from './actions';

type CommentMapById = { [key: string]: CommentEntity };

export interface CommentState {
    [key: string]: CommentEntity;
}

const initialState: CommentState = {};

const commentsSlice = createSlice({
    name: 'comments',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getPostComments.fulfilled, (state, { payload }) => {
            const comments: CommentMapById = {};

            payload.forEach((comment) => (comments[comment.id] = comment));

            return { ...state, ...comments };
        });

        builder.addCase(getPhotoComments.fulfilled, (state, { payload }) => {
            const comments: CommentMapById = {};

            payload.forEach((comment) => (comments[comment.id] = comment));

            return { ...state, ...comments };
        });

        builder.addCase(getCommentReplies.fulfilled, (state, { payload }) => {
            const rootId = payload[0].reply_to_cid;

            if (rootId) {
                if (state[rootId]?.replies) {
                    state[rootId].replies?.push(...payload);
                } else {
                    state[rootId].replies = [];
                    state[rootId].replies?.push(...payload);
                }
            } else {
                return state;
            }
        });

        builder.addCase(createComment.fulfilled, (state, { payload }) => {
            const rootId = payload.reply_to_cid;

            if (rootId) {
                if (!state[rootId].replies) {
                    state[rootId] = { ...state[rootId], replies: [] };
                }

                state[rootId].replies?.push(payload);

                state[rootId].likes_comments.comments = Math.max(0, (state[rootId].likes_comments.comments ?? 1) + 1);
            } else {
                state[payload.id] = payload;
            }
        });

        builder.addCase(createLike.fulfilled, (state, { meta }) => {
            if (meta.arg.entity_type !== 'comment') return;

            const parentCommentId = meta.arg.nested_comment_parent_id;
            const commentId = meta.arg.entity_id;

            if (!parentCommentId && state[commentId].likes_comments) {
                state[commentId].likes_comments.likes = Math.max(0, (state[commentId].likes_comments.likes ?? 1) + 1);

                state[commentId].likes_comments.user_liked = true;
            }

            if (parentCommentId) {
                const rootId = parentCommentId;

                state[rootId] = {
                    ...state[rootId],
                    replies: state[rootId].replies?.map((reply) =>
                        reply.id === commentId
                            ? {
                                  ...reply,
                                  likes_comments: {
                                      ...reply.likes_comments,
                                      likes: reply.likes_comments.likes + 1,
                                      user_liked: true,
                                  },
                              }
                            : reply,
                    ),
                };
            }
        });

        builder.addCase(deleteLike.fulfilled, (state, { meta }) => {
            if (meta.arg.entity_type !== 'comment') return;

            const parentCommentId = meta.arg.nested_comment_parent_id;
            const commentId = meta.arg.entity_id;

            // like deletion in case of top-level comment
            if (!parentCommentId && state[commentId].likes_comments) {
                state[commentId].likes_comments.likes = Math.max(0, (state[commentId].likes_comments.likes ?? 1) - 1);

                state[commentId].likes_comments.user_liked = false;
            }

            // like deletion in case of nested comment
            if (parentCommentId) {
                const rootId = parentCommentId;

                state[rootId] = {
                    ...state[rootId],
                    replies: state[rootId].replies?.map((reply) =>
                        reply.id === commentId
                            ? {
                                  ...reply,
                                  likes_comments: {
                                      ...reply.likes_comments,
                                      likes: reply.likes_comments.likes - 1,
                                      user_liked: false,
                                  },
                              }
                            : reply,
                    ),
                };
            }
        });

        builder.addMatcher(isAnyOf(deleteComment.fulfilled, moderationDeleteComment), (state, { payload }) => {
            if (payload.replyToCid) {
                const rootId = payload.replyToCid;

                state[rootId] = {
                    ...state[rootId],
                    replies: state[rootId].replies?.filter((reply) => reply.id !== payload.id),
                };

                state[rootId].likes_comments.comments = Math.max(0, (state[rootId].likes_comments.comments ?? 1) - 1);
            } else {
                delete state[payload.id];
            }
        });
    },
});

export default commentsSlice.reducer;
